/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime-corejs3/helpers/esm/objectWithoutProperties";
import _typeof from "@babel/runtime-corejs3/helpers/esm/typeof";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime-corejs3/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime-corejs3/helpers/esm/inherits";
import _createSuper from "@babel/runtime-corejs3/helpers/esm/createSuper";
import _defineProperty from "@babel/runtime-corejs3/helpers/esm/defineProperty";

/** @module react/hocs/with-overscroll-blocker */
import { eventListenerObjectSupport, OverscrollBlocker } from '@codistica/browser';
import React from 'react';
var eventOptions = eventListenerObjectSupport.passive ? {
  passive: false
} : undefined;

/**
 * @typedef withOverscrollBlockerPropsType
 * @property {boolean} [blockX=true] - Block overscroll on X axis.
 * @property {boolean} [blockY=true] - Block overscroll on Y axis.
 * @property {boolean} [contentAware=false] - Traverse DOM searching for other scrollable elements to take into consideration.
 * @property {*} [children=null] - React prop.
 * @property {*} [forwardedRef=null] - React prop.
 */

/**
 * @description Creates a higher order component with overscroll blocking capabilities.
 * @param {(Object<string,*>|string)} Component - React component.
 * @returns {Object<string,*>} Created higher order component.
 */
function withOverscrollBlocker(Component) {
  var _this2 = this,
      _context2;

  /**
   * @classdesc Higher order component.
   */
  var HOC = /*#__PURE__*/function (_React$Component) {
    _inherits(HOC, _React$Component);

    var _super = _createSuper(HOC);

    /**
     * @description Constructor.
     * @param {withOverscrollBlockerPropsType} [props] - Component props.
     */
    function HOC(props) {
      var _context;

      var _this;

      _classCallCheck(this, HOC);

      _this = _super.call(this, props);

      _defineProperty(_assertThisInitialized(_this), "componentRef", void 0);

      _defineProperty(_assertThisInitialized(_this), "overscrollBlocker", void 0);

      _defineProperty(_assertThisInitialized(_this), "touchPos", void 0);

      _defineProperty(_assertThisInitialized(_this), "isMultiTouch", void 0);

      _this.componentRef = null;
      _this.overscrollBlocker = new OverscrollBlocker({
        blockX: props.blockX,
        blockY: props.blockY
      });
      _this.touchPos = {
        x: 0,
        y: 0
      };
      _this.isMultiTouch = false; // BIND METHODS

      _assertThisInitialized(_this).setComponentRef = _bindInstanceProperty(_context = _this.setComponentRef).call(_context, _assertThisInitialized(_this));
      return _this;
    }
    /**
     * @instance
     * @description React lifecycle.
     * @returns {void} Void.
     */


    _createClass(HOC, [{
      key: "componentDidMount",
      value: function componentDidMount() {
        this.componentRef.addEventListener('touchstart', this.overscrollBlocker.handler, eventOptions);
        this.componentRef.addEventListener('touchmove', this.overscrollBlocker.handler, eventOptions);
        this.componentRef.addEventListener('touchend', this.overscrollBlocker.handler, eventOptions);
        this.componentRef.addEventListener('wheel', this.overscrollBlocker.handler, eventOptions);
      }
      /**
       * @instance
       * @description React lifecycle.
       * @returns {void} Void.
       */

    }, {
      key: "componentWillUnmount",
      value: function componentWillUnmount() {
        this.componentRef.removeEventListener('touchstart', this.overscrollBlocker.handler, eventOptions);
        this.componentRef.removeEventListener('touchmove', this.overscrollBlocker.handler, eventOptions);
        this.componentRef.removeEventListener('touchend', this.overscrollBlocker.handler, eventOptions);
        this.componentRef.removeEventListener('wheel', this.overscrollBlocker.handler, eventOptions);
      }
      /**
       * @instance
       * @description Save and pass component reference.
       * @param {Object<string,*>} ref - Component reference.
       * @returns {void} Void.
       */

    }, {
      key: "setComponentRef",
      value: function setComponentRef(ref) {
        // FORWARD REF
        var forwardedRef = this.props.forwardedRef;

        if (typeof forwardedRef === 'function') {
          forwardedRef(ref);
        } else if (_typeof(forwardedRef) === 'object' && forwardedRef !== null && typeof forwardedRef.current !== 'undefined') {
          forwardedRef.current = ref;
        } // SAVE REF


        this.componentRef = ref;
      }
      /**
       * @instance
       * @description React render method.
       * @returns {Object<string,*>} React component.
       */

    }, {
      key: "render",
      value: function render() {
        var _this$props = this.props,
            children = _this$props.children,
            blockX = _this$props.blockX,
            blockY = _this$props.blockY,
            contentAware = _this$props.contentAware,
            forwardedRef = _this$props.forwardedRef,
            other = _objectWithoutProperties(_this$props, ["children", "blockX", "blockY", "contentAware", "forwardedRef"]);

        this.overscrollBlocker.options.blockX = blockX;
        this.overscrollBlocker.options.blockY = blockY;
        this.overscrollBlocker.options.contentAware = contentAware;
        return /*#__PURE__*/React.createElement(Component, _extends({}, other, {
          ref: this.setComponentRef
        }), children);
      }
    }]);

    return HOC;
  }(React.Component);

  _defineProperty(HOC, "defaultProps", {
    blockX: true,
    blockY: true,
    contentAware: false,
    children: null,
    forwardedRef: null
  });

  return React.forwardRef(_bindInstanceProperty(_context2 = function _context2(props, ref) {
    _newArrowCheck(this, _this2);

    return /*#__PURE__*/React.createElement(HOC, _extends({}, props, {
      forwardedRef: ref
    }));
  }).call(_context2, this));
}

export { withOverscrollBlocker };